import React, { useEffect } from 'react';
import ExpandLink from 'components/blazeTable/ExpandLink';
import { WithTranslation, withTranslation } from 'react-i18next';
import { CallbackFn, useFormInput, Validator } from 'hooks/useFormInput';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { Configuration } from '../../../types/rest/configurationsType';
import i18n from '../../../i18n';
import Autofocus from '../../../components/forms/Autofocus/Autofocus';

interface ConfigurationFieldCellProps {
  configuration: Configuration;
  onChange: (rowData: Configuration, field: string) => void;
  toggleExpandDetail: (id: number) => void;
  disabled: boolean;
  field: 'name' | 'description';
  fieldValidator: Validator[];
  isExpanded: boolean;
  setInvalidField: (invalid: boolean) => void;
  resetDirtyFieldFound: () => void;
  invalidFieldFound: boolean;
  autoFocus: boolean;
}

class ConfigurationFieldCell extends React.Component<ConfigurationFieldCellProps & WithTranslation> {
  fieldChangedHandler = (field: string, invalidField: boolean) => {
    this.props.setInvalidField(invalidField);
    this.props.onChange(this.props.configuration, field);
  };

  isNewConfiguration = (): boolean => {
    return this.props.configuration.id === -1;
  };

  render() {
    if (this.isNewConfiguration() || this.props.isExpanded) {
      return (
        <FieldInputWithValidationHook
          id={this.props.configuration.id}
          field={this.props.field}
          fieldValue={this.props.configuration[this.props.field]}
          disabled={this.props.disabled}
          fieldChangedHandler={this.fieldChangedHandler}
          setInvalidField={this.props.setInvalidField}
          placeholder={
            this.props.field === 'name'
              ? i18n.t('configurations:configurationsTable.columnNames.namePlaceholder')
              : i18n.t('configurations:configurationsTable.columnNames.descriptionPlaceholder')
          }
          fieldValidators={this.props.fieldValidator}
          autoFocus={this.props.autoFocus}
          invalidFieldFound={this.props.invalidFieldFound}
          resetDirtyFieldFound={this.props.resetDirtyFieldFound}
        />
      );
    } else {
      const displayedId = this.props.configuration.id;
      const title = `Configuration (ID: ${displayedId})`;
      if (this.props.field === 'name') {
        return (
          <ExpandLink
            value={this.props.configuration.name}
            id={this.props.configuration.id}
            title={title}
            onToggleExpandDetail={() => {
              if (this.props.isExpanded === false) {
                this.props.toggleExpandDetail(this.props.configuration.id);
              }
            }}
          />
        );
      } else {
        return (
          <div className={'text-truncate'}>
            <p style={{ marginBottom: '1px' }}>{this.props.configuration.description}</p>
          </div>
        );
      }
    }
  }
}

export default withTranslation('mockServices')(ConfigurationFieldCell);

/**
 * Input validation hook. It is functional component wrapping BzFormControlWithHook which cannot be
 * used inside of the stateful component.
 * @constructor
 */
const FieldInputWithValidationHook = (props: {
  field: string;
  fieldValue: string;
  disabled: boolean;
  id: number;
  fieldChangedHandler: any;
  placeholder: string;
  fieldValidators: Validator[];
  setInvalidField: (invalid: boolean) => void;
  invalidFieldFound: boolean;
  resetDirtyFieldFound: () => void;
  autoFocus: boolean;
}) => {
  /**
   * Use effect for applying changes coming from outside.
   * */
  useEffect(() => {
    if (props.field === 'name' && props.invalidFieldFound === true && fieldInput.dirty === false) {
      fieldInput.validate(props.fieldValue);
    }
    if (props.invalidFieldFound === true) {
      props.resetDirtyFieldFound();
    }
  }, [props.invalidFieldFound]);

  const fieldInput = useFormInput(
    props.fieldValue,
    (fn: CallbackFn) => {
      //Do update
      props.fieldChangedHandler(fn.value, !fn.isValid);
    },
    ...props.fieldValidators,
  );

  const getFromInput = () => (
    <BzFormControlWithHook
      className={'ConfigurationName-' + props.id}
      useFormInputHook={fieldInput}
      placeholder={props.placeholder}
      required={true}
      value={props.fieldValue}
      disabled={props.disabled}
      autoComplete={'false'}
    />
  );

  return <>{props.autoFocus ? <Autofocus>{getFromInput()}</Autofocus> : getFromInput()}</>;
};
